import Config from './config';
import { device } from 'vimmi-web-utils/cjs/device';
import { deviceAsync } from 'vimmi-web-utils/cjs/deviceAsync';
import videojs from 'video.js';
import TechSrv from './techSrv';
import vimmiPI from '../plugins/vimmiPI';
import Analytics from '../plugins/analytics';
import autoplayState from './autoplayState';
import eventsSrv from './events';
import Preview from './preview';
import { PLAYER_EVENTS } from 'vimmi-web-utils/cjs/postMessage';
import { PMWrap } from './PMWrap';
import PlayerSrv from 'src/js/services/playerSrv';
import { normalizeLang } from 'vimmi-web-utils/cjs/normalizeLang';
import Languages from './languages';
import ads from '../plugins/ads';
import Restriction from 'src/js/services/restriction';
import { PrevNextButtonService } from 'src/js/vjsComponents/prevNextButton';
import { QualitySelectorService } from 'src/js/vjsComponents/quality-selector';
import { deepMerge } from 'vimmi-web-utils/cjs/deepMerge';
import $logger from '../logger';

async function deviceInfo() {
  $logger.force().p('deviceInfo').debug({
    info: device.info,
    DeviceType: await deviceAsync.getDeviceType(),
    OsName: await deviceAsync.getOsName(),
    BrowserName: await deviceAsync.getBrowserName(),
    OsVersion: await deviceAsync.getOsVersion(),
    isAppleDevice: await deviceAsync.isAppleDevice(),
    isIOS: await deviceAsync.isIOS(),
    isIpad: await deviceAsync.isIpad(),
    isAndroid: await deviceAsync.isAndroid()
  });
}

window.deviceInfo = deviceInfo;

let hookInitialized = false;

class VjsService {
  KEYS = {
    REM_PLAY: 415, // remote play
    REM_PAUSE: 19, // remote pause
    REM_STOP: 413, // remote stop
    REM_REWIND_RIGHT: 417, // remote rewind right
    REM_REWIND_LEFT: 412, // remote rewind left

    PLAY: 179, // play/pause
    SPACE: 32, // space
    ENTER: 13, // enter/ remote ok

    RIGHT: 39, // RIGHT/ remote right
    LEFT: 37, // LEFT/ remote left
    TOP: 38, // TOP/ remote up
    DOWN: 40, // DOWN/  remote down
    BACK: 461 // BACK/  remote BACK
  };

  constructor() {
    Languages.init();
    this.vjsPlayer = null;
    this.readyCllbacks = [];
  }

  async autoplayBrowserAllow() {
    return (vimmiPI.getActiveAsset().get('playerOptions.autoplay') || vimmiPI.getActiveAsset().get('state.changingAudio')) && await autoplayState.checkAutoPlayState();
  }

  async mutedAutoplayBrowserAllow() {
    return vimmiPI.getActiveAsset().get('playerOptions.autoplay') && (vimmiPI.getActiveAsset().get('playerOptions.forceAutoplayMuted') || vimmiPI.getActiveAsset().get('playerOptions.muted')) && await autoplayState.checkMutedAutoPlayState();
  }

  createPlayer(options) {

    let buffLen = options?.videoJsOptions?.GOAL_BUFFER_LENGTH;
    let buffMaxLen = options?.videoJsOptions?.MAX_GOAL_BUFFER_LENGTH;
    if (buffLen) {
      delete options.videoJsOptions.GOAL_BUFFER_LENGTH;
      videojs.Vhs.GOAL_BUFFER_LENGTH = buffLen;
    }
    if (buffMaxLen) {
      delete options.videoJsOptions.MAX_GOAL_BUFFER_LENGTH;
      videojs.Vhs.MAX_GOAL_BUFFER_LENGTH = buffMaxLen;
    }

    return new Promise(async (resolve) => {
      const langCode = normalizeLang(Config.get('params.lang'), 'iso-2');
      if (langCode) {
        Config.set('vjsOptions.language', langCode);
      }
      PlayerSrv.addTag(options);
      let fullOpt = await this._handleVideoJsOption(options);
      TechSrv.xhrHook();
      $logger.p('create').debug(fullOpt);
      window.vjsPlayer = this.vjsPlayer = videojs(Config.get('playerId'), fullOpt, () => {
        this.vjsPlayer.TechSrv = new TechSrv();
        this.vjsPlayer.PrevNextButtonSrv = new PrevNextButtonService();
        this.vjsPlayer.QualitySelectorSrv = this.vjsPlayer.QualitySelectorSrv || new QualitySelectorService();

        if (!Config.get('params.imported')) {
          PMWrap.inst().setPlayer(this.vjsPlayer);
        }

        if (!Config.get('params.controls')) {
          this.vjsPlayer.getChild('controlBar').hide();
        }

        while (this.readyCllbacks.length) {
          (this.readyCllbacks.shift())(this.vjsPlayer);
        }
        resolve(this.vjsPlayer);
      });
    });
  }

  /**
   * @method tryPlay
   * @description force try to start play video if browser supports autoplay, and if option 'autoplay' is true
   * */
  async tryPlay() {
    let allowedPlayMuted = vimmiPI.getActiveAsset().get('playerOptions.autoplay') && (vimmiPI.getActiveAsset().get('playerOptions.muted') || vimmiPI.getActiveAsset().get('playerOptions.forceAutoplayMuted')) && await autoplayState.checkMutedAutoPlayState();

    if (Config.get('eventsState.isCallPlay') || allowedPlayMuted || await autoplayState.checkAutoPlayState()) {
      this.vjsPlayer.play();
      return true;
    } else {
      $logger.p('tryPlay').warn('play not allowed!', {
        allowedPlayMuted,
        isMutedAutoplay: await autoplayState.checkMutedAutoPlayState(),
        isAutoplay: await autoplayState.checkAutoPlayState()
      });
      return false;
    }
  }

  /**
   * @method getPromise
   * @description return promise for player, in this case will returned after player created
   * @return {promise}
   * */
  getPromise() {
    return new Promise((resolve) => {
      if (this.vjsPlayer) {
        resolve(this.vjsPlayer);
      } else {
        this.readyCllbacks.push(resolve);
      }
    });
  }

  async resetPlayer() {
    if (!vjsSrv.vjsPlayer || vjsSrv.vjsPlayer.isDisposed()) {
      return;
    }
    if (vjsSrv.vjsPlayer.currentSrc()) {
      return new Promise(async (resolve) => {
        this.vjsPlayer.pause();
        let volumeLevel = this.vjsPlayer.volume();
        let muted = this.vjsPlayer.muted();
        if (await deviceAsync.isIOS()) {
          let asset = vimmiPI.getActiveAsset();
          if (!Restriction.isRestricted(asset)) {
            this.vjsPlayer.one('loadstart', () => {
              asset = vimmiPI.getActiveAsset();
              let iosExclusion = ads.isBuggedIos && (asset?.adTag || asset?.adVast) && !ads.isAdDisplayContainerInit;
              if (muted || iosExclusion) {
                this.vjsPlayer.muted(true);
                this.vjsPlayer.volume(0);
              } else {
                this.vjsPlayer.muted(false);
                this.vjsPlayer.volume(volumeLevel);
              }
            });
          }
          eventsSrv.emit('player_reset');
          resolve(true);
        } else if (deviceAsync.isSmartTv() || await deviceAsync.isFirefox() || await deviceAsync.isChrome() || await deviceAsync.isMacOs() || await deviceAsync.isEdge()) {
          this.vjsPlayer.one('playerreset', () => {
            $logger.debug('playerreset', { muted, volumeLevel, isBuggedIos: ads.isBuggedIos });
            this.vjsPlayer.one('loadstart', () => {
              if (muted /*|| iosExclusion*/) {
                this.vjsPlayer.muted(true);
                this.vjsPlayer.volume(0);
              } else {
                this.vjsPlayer.muted(false);
                this.vjsPlayer.volume(volumeLevel);
              }
            });
            eventsSrv.emit('player_reset');
            resolve(true);
          });
          this.vjsPlayer.reset();
        } else {
          eventsSrv.emit('player_reset');
          resolve(false);
        }
      });
    } else {
      return true;
    }
  }

  controls(state) {
    this.vjsPlayer.controls(state);
  }

  /**
   * @param event {Event}
   * @param btn {'PlayToggle'|'BigPlayButton'}
   * @param targetState {'paused'|'playing'} - detect the state before btn click
   *
   * */
  onPlayBntClick(event, btn, targetState) {
    if (this.vjsPlayer.waitingAsset) {
      eventsSrv.emit('_internal-play', {
        asset: this.vjsPlayer.waitingAsset,
        isFullAsset: false
      });
      this.vjsPlayer.waitingAsset = null;
      return true;
    }

    if (!Config.get('params.controls')) {
      return false;
    }

    if (Config.get('eventsState.started') && targetState === 'paused') {
      this.onPaused(true);
      // Analytics.send('chu', {}, { force: true });
      // Analytics.send('media_pause', {
      //   stop_cause: 'pause'
      // });
      $logger.info('@user action: PAUSE', btn, event);
      if (btn === 'PlayToggle') {
        this.vjsPlayer.pause();
      }
    } else if (targetState === 'playing') {
      this.onStarted(true);
      if (btn === 'PlayToggle') {
        this.vjsPlayer.play();
      }
    }
  }

  /**
   * @param event {Event}
   * @param targetState {'paused'|'playing'} - detect the state before btn click
   *
   * */
  async onPlayBntWrapperClick(event, targetState = 'paused') {
    if (!Config.get('params.controls')) {
      return false;
    }
    if (targetState === 'paused' && !(this.vjsPlayer.hasClass('vjs-waiting') || this.vjsPlayer.hasClass('custom-waiting-video')) &&
      (!(await deviceAsync.isAnyMobile()) || (Config.get('eventsState.prevUserActiveState') && (await deviceAsync.isAnyMobile())))) {

      // Analytics.send('chu', {}, { force: true });
      // Analytics.send('media_pause', {
      //   stop_cause: 'pause'
      // });

      $logger.p('userAction/wrapper').log('pause');

      this.onPaused(true);
      if (targetState === 'paused') {
        this.vjsPlayer.pause();
      }
    } else if (targetState === 'playing' && !(this.vjsPlayer.hasClass('vjs-waiting') || this.vjsPlayer.hasClass('custom-waiting-video')) && Config.get('params.playOnWrapClick')) {
      this.onStarted(true);
      this.vjsPlayer.play();
    }
  }

  onPaused(userAction) {
    vimmiPI.concurStop();
    vimmiPI.getActiveAsset().set('state.pause', true);
    if (vimmiPI.getActiveAsset().get('state.resumed')) {
      vimmiPI.getActiveAsset().set('state.resumed', false);
      $logger.p('onPaused/pause').log({
        from: vimmiPI.getActiveAsset() && vimmiPI.getActiveAsset().get('extraData.active_position'),
        duration: this.vjsPlayer.duration(),
        currentTime: this.vjsPlayer.currentTime(),
        eventsState: Config.get('eventsState'),
        state: vimmiPI.getActiveAsset() && vimmiPI.getActiveAsset().get('state'),
        userAction
      });
      if (userAction) {
        Analytics.send('chu', {}, { force: true });
        Analytics.send('media_pause', {
          stop_cause: 'pause'
        });
      }
      eventsSrv.emit(PLAYER_EVENTS.USER_PAUSE, { userAction });
      if (!Config.get('params.imported')) {
        PMWrap.inst().sendWrappedEvent(PLAYER_EVENTS.USER_PAUSE, { userAction });
      }
    }
  }

  async onStarted(userAction) {
    vimmiPI.concurPlay();
    Config.set('eventsState.waiting', false);
    vimmiPI.getActiveAsset().set('state.pause', false);
    if (!Config.get('eventsState.started')) {
      Config.set('eventsState.started', true);
    }
    if (!vimmiPI.getActiveAsset().get('state.started')) {
      const autoplay = await this.autoplayBrowserAllow() || await this.mutedAutoplayBrowserAllow();
      vimmiPI.getActiveAsset().set('extraData.isAutoPlayed', autoplay);
      $logger.p('onStarted/start').log({
        from: vimmiPI.getActiveAsset() && vimmiPI.getActiveAsset().get('extraData.active_position'),
        duration: this.vjsPlayer.duration(),
        currentTime: this.vjsPlayer.currentTime(),
        eventsState: Config.get('eventsState'),
        state: vimmiPI.getActiveAsset() && vimmiPI.getActiveAsset().get('state'),
        userAction,
        autoplay
      });
      vimmiPI.getActiveAsset().set('state.started', true);
      vimmiPI.getActiveAsset().set('state.resumed', true); // to prevent sending event till user click pause


      if (userAction || autoplay) {
        let currentBitrate = vimmiPI.getActiveAsset().get('extraData.active_bitrate', 0);
        if (currentBitrate > vimmiPI.getActiveAsset().get('extraData.actual_max_bitrate', 0)) {
          vimmiPI.getActiveAsset().set('extraData.actual_max_bitrate', currentBitrate);
        }
        Analytics.send('start');
      }

      eventsSrv.emit(PLAYER_EVENTS.USER_PLAY, {userAction});
      if (!Config.get('params.imported')) {
        PMWrap.inst().sendWrappedEvent(PLAYER_EVENTS.USER_PLAY, { userAction, ...vimmiPI.getActiveAsset() });
      }
    } else if (!vimmiPI.getActiveAsset().get('state.resumed')) {
      vimmiPI.getActiveAsset().set('state.resumed', true);
      $logger.p('onStarted/resume').log({
        from: vimmiPI.getActiveAsset() && vimmiPI.getActiveAsset().get('extraData.active_position'),
        duration: this.vjsPlayer.duration(),
        currentTime: this.vjsPlayer.currentTime(),
        eventsState: Config.get('eventsState'),
        state: vimmiPI.getActiveAsset() && vimmiPI.getActiveAsset().get('state'),
        userAction
      });
      if (userAction) {
        Analytics.send('media_resume');
      }

      eventsSrv.emit(PLAYER_EVENTS.USER_RESUME, { userAction });
      if (!Config.get('params.imported')) {
        PMWrap.inst().sendWrappedEvent(PLAYER_EVENTS.USER_RESUME, { userAction });
      }
    }
  }


  vjsUserActions(options) {
    return {
      // click: (event) => {
      //   console.log(1111, 'click', event);
      // },
      // doubleClick: (event) => {
      //   console.log(1111, 'doubleClick', event);
      // },
      hotkeys: {
        playPauseKey: (event) => {
          if (!vimmiPI.getActiveAsset() || ads.playing()) {
            return false;
          }

          if (Preview.isShowPreview(vimmiPI.getActiveAsset()) && Preview.ended) {
            return false;
          }
          if ([32, 179].includes(event.which)) {
            if (!this.vjsPlayer.paused()) {
              this.onPaused(true);
            } else {
              this.onStarted(true);
            }
            return true;
          }
          return false;
        }
      }
    };
  }

  async _handleVideoJsOption(options = {}) {
    options.controlBar = options.controlBar || {};
    let vjsOptions = Config.get('vjsOptions') || {};
    vjsOptions.userActions = this.vjsUserActions(options);

    // vjsOptions.liveuiliveui = false;
    // vjsOptions.liveTracker = false;

    if (options.plugins) {
      vjsOptions.plugins = deepMerge(vjsOptions.plugins || {}, options.plugins);
    }


    /** replace bigPlayBtn by bigPlayButton */
    let vjsChildren = [...videojs.options.children];
    vjsChildren.splice(vjsChildren.indexOf('bigPlayButton'), 1, 'WrapperBigPlayBtn');
    vjsOptions.children = vjsChildren;

    vjsOptions.controlBar = vjsOptions.controlBar || {};
    vjsOptions.controlBar.children = vjsOptions.controlBar.children || [];

    let html5 = {
      vhs: {}
    };


    if (Config.get('params.dash')) {
      $logger.force().warn('Please use "html5.dash" instead "dash"!');
      html5.dash = Config.get('params.dash');
    }

    if ((await deviceAsync.isIOS() || await deviceAsync.isMacOs()) && (await deviceAsync.isChrome() || await deviceAsync.isSafari())) {
      html5.vhs.overrideNative = false;
      html5.nativeAudioTracks = true;
      if (await deviceAsync.isIOS()) {
        html5.nativeVideoTracks = false;
      } else if (await deviceAsync.isMacOs()) {
        html5.nativeVideoTracks = true;
      }
    }

    vjsOptions = deepMerge(vjsOptions || {}, { html5 }, options?.videoJsOptions || {});

    if (Config.get('params.controls')) {
      let barChildren = [];
      barChildren.push('PrevButton');
      if (options.controlBar.PlayToggle === undefined || options.controlBar.PlayToggle) {
        barChildren.push('PlayToggleBtn');
      }
      barChildren.push('NextButton');
      barChildren.push('currentTimeDisplay');
      barChildren.push('timeDivider');
      barChildren.push('durationDisplay');

      if (options.controlBar.VolumePanel === undefined || options.controlBar.VolumePanel) {
        if (await deviceAsync.isAnyMobile() || await deviceAsync.isIpad() || (/Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1)) {
          barChildren.push('MuteToggle');
        } else {
          barChildren.push('VolumePanel');
        }
      } else {
        vjsOptions.controlBar.MuteToggle = false;
        vjsOptions.controlBar.VolumeControl = false;
        vjsOptions.controlBar.VolumePanel = false;
      }

      barChildren.push('remainingTimeDisplay');
      barChildren.push('liveDisplay');
      barChildren.push('progressControl');


      if (options.controlBar.QualitySelectorButton) {
        barChildren.push('QualitySelectorButton');
      }
      if (options.controlBar.AudioTrackButton) {
        barChildren.push('AudioTrackButton');
        barChildren.push('CustomAudioTrackButton');
      }
      if (options.controlBar.SubtitlesButton) {
        barChildren.push('SubtitlesButton');
      }
      if (options.controlBar.FullscreenToggle && !Config.get('params.audio_tag')) {
        barChildren.push('FullscreenToggle');
      } else {
        Config.set('vjsOptions.controlBar.FullscreenToggle', false);
      }
      vjsOptions.controlBar.children = barChildren;
    }

    Config.set('vjsOptions', vjsOptions);

    return Config.get('vjsOptions');
  }

  initVideoJsHooks() {
    if (!hookInitialized) {
      hookInitialized = true;
      videojs.use('*', (player) => {
        return {
          callPlay: () => {
            if (!vimmiPI.getActiveAsset() || player.isDisposed()) {
              return;
            }
            // $logger.p('videojs/callPlay').info('');
            // vimmiPI.getActiveAsset().set('state.callPlay', true);
            Config.set('eventsState.isCallPlay', true);

            if (Config.get('eventsState.started') && Config.get('eventsState.ended')) {
              Config.set('eventsState.started', false);
              vimmiPI.getActiveAsset().set('state.started', false);
              vimmiPI.getActiveAsset().set('state.playing', false);
              // Config.set('eventsState.playing', false);
              Config.set('eventsState.ended', false);
              Analytics.resetCalculatedOptions(vimmiPI.getActiveAsset());
            }

            // Do nothing, thereby allowing play() to be called on the Tech
          },
          play: (terminated, value) => {
            if (!vimmiPI.getActiveAsset() || player.isDisposed()) {
              return;
            }
            if (terminated) {
              $logger.p('videojs/play').warn('The play was middleware terminated.');
            } else if (value && value.then) {
              value
                .then(() => {
                  if (!vimmiPI.getActiveAsset()) {
                    return;
                  }
                  // if (!Config.get('eventsState.started')) {
                  //   Config.set('eventsState.started', true);
                  //   vimmiPI.getActiveAsset().set('extraData.isAutoPlayed', true);
                  // }
                  $logger.p('videojs/play').info('The play succeeded!');
                  // vjsSrv.onPlayerStart();
                })
                .catch((err) => {
                  // if (vimmiPI.getActiveAsset()) {
                  //   vimmiPI.getActiveAsset().set('extraData.isAutoPlayed', false);
                  // }

                  // Config.set('eventsState.started', false);
                  $logger.p('videojs/play').warn('The play was rejected!', err);
                });
            } else {
              $logger.p('videojs/play').warn('play promise not supports! it should start to play');
              // Config.set('eventsState.started', true);
              // if (vimmiPI.getActiveAsset()) {
              //   vimmiPI.getActiveAsset().set('extraData.isAutoPlayed', true);
              // }
              // vjsSrv.onPlayerStart();
            }
          }
        };
      });
    }
  }


  destroy() {
    this.vjsPlayer.dispose();
    this.vjsPlayer.TechSrv.destroy();
    this.vjsPlayer.TechSrv = null;
    this.vjsPlayer = null;
    window.vjsPlayer = null;
  }
}

const vjsSrv = new VjsService();

export default vjsSrv;


